import {
  AssistantMessage,
  Message as MessageType,
  Part as PartType,
} from "@opencode-ai/sdk/v2/client"
import type { SessionStatus } from "@opencode-ai/sdk/v2"
import { useData } from "../context"

import { Binary } from "@opencode-ai/core/util/binary"
import { createEffect, createMemo, createSignal, For, on, onCleanup, ParentProps, Show } from "solid-js"
import { AssistantParts, isMemoryChipPart, Message, MessageDivider, renderable as isAssistantPartRenderable, type UserActions } from "./message-part"
import { Card } from "./card"
import { Icon } from "./icon"
import { SessionRetry } from "./session-retry"
import { currentChecks } from "./session-turn-completion"
import { createAutoScroll } from "../hooks"
import { useI18n } from "../context/i18n"

function isThinkingLayerPart(part: PartType) {
  return part.type === "reasoning" || part.type === "tool"
}

function isThinkingTimelineBoundary(part: PartType, showReasoningSummaries: boolean) {
  if (part.type === "tool") return true
  return isAssistantPartRenderable(part, showReasoningSummaries) && part.type === "reasoning"
}

function ThinkingStatus(props: { working: boolean; createdAt?: number; durationMs?: number }) {
  const i18n = useI18n()
  const [now, setNow] = createSignal(Date.now())
  createEffect(
    on(
      () => props.working,
      (working) => {
        if (!working) return
        setNow(Date.now())
        const timer = setInterval(() => setNow(Date.now()), 100)
        onCleanup(() => clearInterval(timer))
      },
    ),
  )
  const numfmt = createMemo(() => new Intl.NumberFormat(i18n.locale()))
  const elapsedSecondsFormatter = createMemo(
    () => new Intl.NumberFormat(i18n.locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  )
  const elapsedMs = createMemo(() => {
    if (props.working && typeof props.createdAt === "number") return Math.max(0, now() - props.createdAt)
    if (!props.working && typeof props.durationMs === "number") return Math.max(0, props.durationMs)
    return undefined
  })
  const elapsed = createMemo(() => {
    const total = elapsedMs()
    if (typeof total !== "number") return ""
    if (total < 60_000) {
      return i18n.t("ui.message.duration.seconds", { count: elapsedSecondsFormatter().format(total / 1000) })
    }
    return i18n.t("ui.message.duration.minutesSeconds", {
      minutes: numfmt().format(Math.floor(total / 60_000)),
      seconds: elapsedSecondsFormatter().format((total % 60_000) / 1000),
    })
  })
  const label = () => i18n.t(props.working ? "ui.sessionTurn.status.working" : "ui.sessionTurn.status.completed")
  const text = createMemo(() => {
    if (!elapsed()) return label()
    return `${label()} • ${elapsed()}`
  })

  return (
    <span data-slot="session-turn-thinking-label" aria-label={text()}>
      {text()}
    </span>
  )
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function unwrap(message: string) {
  const text = message.replace(/^Error:\s*/, "").trim()

  const parse = (value: string) => {
    try {
      return JSON.parse(value) as unknown
    } catch {
      return undefined
    }
  }

  const read = (value: string) => {
    const first = parse(value)
    if (typeof first !== "string") return first
    return parse(first.trim())
  }

  let json = read(text)

  if (json === undefined) {
    const start = text.indexOf("{")
    const end = text.lastIndexOf("}")
    if (start !== -1 && end > start) {
      json = read(text.slice(start, end + 1))
    }
  }

  if (!record(json)) return message

  const err = record(json.error) ? json.error : undefined
  if (err) {
    const type = typeof err.type === "string" ? err.type : undefined
    const msg = typeof err.message === "string" ? err.message : undefined
    if (type && msg) return `${type}: ${msg}`
    if (msg) return msg
    if (type) return type
    const code = typeof err.code === "string" ? err.code : undefined
    if (code) return code
  }

  const msg = typeof json.message === "string" ? json.message : undefined
  if (msg) return msg

  const reason = typeof json.error === "string" ? json.error : undefined
  if (reason) return reason

  return message
}

function same<T>(a: readonly T[], b: readonly T[]) {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((x, i) => x === b[i])
}

function list<T>(value: T[] | undefined | null, fallback: T[]) {
  if (Array.isArray(value)) return value
  return fallback
}

export function SessionTurn(
  props: ParentProps<{
    sessionID: string
    messageID: string
    messages?: MessageType[]
    actions?: UserActions
    showReasoningSummaries?: boolean
    shellToolDefaultOpen?: boolean
    editToolDefaultOpen?: boolean
    active?: boolean
    status?: SessionStatus
    onUserInteracted?: () => void
    classes?: {
      root?: string
      content?: string
      container?: string
    }
  }>,
) {
  const data = useData()
  const i18n = useI18n()

  const emptyMessages: MessageType[] = []
  const emptyParts: PartType[] = []
  const emptyAssistant: AssistantMessage[] = []
  const idle = { type: "idle" as const }

  const allMessages = createMemo(() => props.messages ?? list(data.store.message?.[props.sessionID], emptyMessages))

  const messageIndex = createMemo(() => {
    const messages = allMessages() ?? emptyMessages
    const result = Binary.search(messages, props.messageID, (m) => m.id)

    const index = result.found ? result.index : messages.findIndex((m) => m.id === props.messageID)
    if (index < 0) return -1

    const msg = messages[index]
    if (!msg || msg.role !== "user") return -1

    return index
  })

  const message = createMemo(() => {
    const index = messageIndex()
    if (index < 0) return undefined

    const messages = allMessages() ?? emptyMessages
    const msg = messages[index]
    if (!msg || msg.role !== "user") return undefined

    return msg
  })

  const pending = createMemo(() => {
    if (typeof props.active === "boolean") return
    const messages = allMessages() ?? emptyMessages
    return messages.findLast(
      (item): item is AssistantMessage => item.role === "assistant" && typeof item.time.completed !== "number",
    )
  })

  const pendingUser = createMemo(() => {
    const item = pending()
    if (!item?.parentID) return
    const messages = allMessages() ?? emptyMessages
    const result = Binary.search(messages, item.parentID, (m) => m.id)
    const msg = result.found ? messages[result.index] : messages.find((m) => m.id === item.parentID)
    if (!msg || msg.role !== "user") return
    return msg
  })

  const active = createMemo(() => {
    if (typeof props.active === "boolean") return props.active
    const msg = message()
    const parent = pendingUser()
    if (!msg || !parent) return false
    return parent.id === msg.id
  })

  const parts = createMemo(() => {
    const msg = message()
    if (!msg) return emptyParts
    return list(data.store.part?.[msg.id], emptyParts)
  })

  const compaction = createMemo(() => parts().find((part) => part.type === "compaction"))

  const assistantMessages = createMemo(
    () => {
      const msg = message()
      if (!msg) return emptyAssistant

      const messages = allMessages() ?? emptyMessages
      if (messageIndex() < 0) return emptyAssistant

      const result: AssistantMessage[] = []
      for (let i = 0; i < messages.length; i++) {
        const item = messages[i]
        if (!item) continue
        if (item.role === "assistant" && item.parentID === msg.id) result.push(item as AssistantMessage)
      }
      return result
    },
    emptyAssistant,
    { equals: same },
  )
  const showReasoningSummaries = createMemo(() => props.showReasoningSummaries ?? true)
  const assistantPartEntries = createMemo(() =>
    assistantMessages().flatMap((message) =>
      list(data.store.part?.[message.id], emptyParts).map((part) => ({
        messageID: message.id,
        part,
      })),
    ),
  )
  const thinkingLayerPartIDs = createMemo(() => {
    const ids = new Set<string>()
    let hasLaterThinkingBoundary = false
    const entries = assistantPartEntries()
    const showReasoning = showReasoningSummaries()

    for (let i = entries.length - 1; i >= 0; i--) {
      const part = entries[i]!.part
      if (isThinkingLayerPart(part)) {
        // Keep the saved-memory chip out of the thinking layer so it renders as
        // persistent answer content, but still treat it as a boundary so any
        // narration text emitted before it stays collapsed.
        if (isMemoryChipPart(part)) {
          hasLaterThinkingBoundary = true
          continue
        }
        if (isAssistantPartRenderable(part, showReasoning)) ids.add(part.id)
        if (isThinkingTimelineBoundary(part, showReasoning)) hasLaterThinkingBoundary = true
        continue
      }
      if (part.type === "text" && part.text?.trim() && hasLaterThinkingBoundary) ids.add(part.id)
    }

    return ids
  })
  const isThinkingTimelinePart = (part: PartType) => thinkingLayerPartIDs().has(part.id)
  const isAssistantContentPart = (part: PartType) => !isThinkingTimelinePart(part)

  const interrupted = createMemo(() => assistantMessages().some((m) => m.error?.name === "MessageAbortedError"))
  const completion = createMemo(() => assistantMessages().findLast((item) => item.completion)?.completion)
  const completionChecks = createMemo(() => {
    const evidence = completion()
    if (!evidence) return []
    const requirementCommands = new Set(evidence.requirements.map((requirement) => requirement.command.trim()))
    return currentChecks(evidence.checks).filter((check) => !requirementCommands.has(check.command.trim()))
  })
  const completionDetailCount = createMemo(() => completionChecks().length + (completion()?.requirements.length ?? 0))
  const completionHasDetails = createMemo(
    () => completionDetailCount() > 0 || (completion()?.review.status ?? "not_run") !== "not_run",
  )
  const completionLabel = createMemo(() => {
    const evidence = completion()
    if (!evidence) return ""
    return i18n.t(`ui.sessionTurn.verification.${evidence.verdict}`)
  })
  const [completionOpen, setCompletionOpen] = createSignal(false)
  const divider = createMemo(() => {
    if (compaction()) return i18n.t("ui.messagePart.compaction")
    if (interrupted()) return i18n.t("ui.message.interrupted")
    return ""
  })
  const error = createMemo(
    () => assistantMessages().find((m) => m.error && m.error.name !== "MessageAbortedError")?.error,
  )
  const showAssistantCopyPartID = createMemo(() => {
    const messages = assistantMessages()

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (!message) continue

      const parts = list(data.store.part?.[message.id], emptyParts)
      for (let j = parts.length - 1; j >= 0; j--) {
        const part = parts[j]
        if (!part || part.type !== "text" || !part.text?.trim()) continue
        return part.id
      }
    }

    return undefined
  })
  const errorText = createMemo(() => {
    const msg = error()?.data?.message
    if (typeof msg === "string") return unwrap(msg)
    if (msg === undefined || msg === null) return ""
    // oxlint-disable-next-line no-base-to-string -- msg is unknown from error data, coercion is intentional
    return unwrap(String(msg))
  })

  const status = createMemo(() => {
    if (props.status !== undefined) return props.status
    if (typeof props.active === "boolean" && !props.active) return idle
    return data.store.session_status[props.sessionID] ?? idle
  })
  const working = createMemo(() => status().type !== "idle" && active())

  const assistantCopyPartID = createMemo(() => {
    if (working()) return null
    return showAssistantCopyPartID() ?? null
  })
  const turnDurationMs = createMemo(() => {
    const start = message()?.time.created
    if (typeof start !== "number") return undefined

    const end = assistantMessages().reduce<number | undefined>((max, item) => {
      const completed = item.time.completed
      if (typeof completed !== "number") return max
      if (max === undefined) return completed
      return Math.max(max, completed)
    }, undefined)

    if (typeof end !== "number") return undefined
    if (end < start) return undefined
    return end - start
  })
  const assistantDerived = createMemo(() => {
    let answer = false
    let assistantContent = false
    let awaitingQuestion = false
    let toolActive = false
    const thinkingIDs = thinkingLayerPartIDs()
    for (const message of assistantMessages()) {
      for (const part of list(data.store.part?.[message.id], emptyParts)) {
        const inThinkingLayer = thinkingIDs.has(part.id)
        if (isAssistantPartRenderable(part, showReasoningSummaries()) && !inThinkingLayer) {
          assistantContent = true
        }
        if (part.type === "text" && part.text?.trim() && !inThinkingLayer) {
          answer = true
        }
        if (part.type === "tool" && part.tool === "question") {
          awaitingQuestion = part.state.status === "pending" || part.state.status === "running"
        }
        if (
          part.type === "tool" &&
          part.tool !== "question" &&
          (part.state.status === "pending" || part.state.status === "running")
        ) {
          toolActive = true
        }
      }
    }
    return { answer, assistantContent, awaitingQuestion, toolActive }
  })
  const showThinking = createMemo(() => {
    if (!working() || !!error()) return false
    if (status().type === "retry") return false
    if (assistantDerived().awaitingQuestion) return false
    // A tool/subagent is still executing (e.g. deep-research fanning out to
    // web-researcher subagents). Keep the progress loader up even if the model
    // already emitted some intermediate text — the turn isn't done answering.
    // Without this the loader vanishes the moment any text part appears, leaving
    // the in-chat view static while the sidebar (driven by session_status) stays
    // lit, so the two disagree.
    if (assistantDerived().toolActive) return true
    return !assistantDerived().answer
  })
  const hasThinkingLayer = createMemo(() =>
    assistantPartEntries().some(
      (entry) => thinkingLayerPartIDs().has(entry.part.id) && isAssistantPartRenderable(entry.part, showReasoningSummaries()),
    ),
  )
  const showThinkingDropdown = createMemo(() => showThinking() || hasThinkingLayer())
  const answerReady = createMemo(() => !working() && assistantDerived().assistantContent)
  const [thinkingOpen, setThinkingOpen] = createSignal(false)
  const [thinkingTouched, setThinkingTouched] = createSignal(false)
  createEffect(
    on(hasThinkingLayer, (available) => {
      if (!available) {
        setThinkingOpen(false)
        setThinkingTouched(false)
        return
      }
      if (!thinkingTouched()) setThinkingOpen(true)
    }),
  )
  createEffect(
    on(answerReady, (ready) => {
      if (ready) setThinkingOpen(false)
    }),
  )

  const autoScroll = createAutoScroll({
    working,
    onUserInteracted: props.onUserInteracted,
    overflowAnchor: "dynamic",
  })

  return (
    <div data-component="session-turn" class={props.classes?.root}>
      <div
        ref={autoScroll.scrollRef}
        onScroll={autoScroll.handleScroll}
        data-slot="session-turn-content"
        class={props.classes?.content}
      >
        <div onClick={autoScroll.handleInteraction}>
          <Show when={message()}>
            <div
              ref={autoScroll.contentRef}
              data-message={message()!.id}
              data-slot="session-turn-message-container"
              class={props.classes?.container}
            >
              <div data-slot="session-turn-message-content" aria-live="off">
                <Message message={message()!} parts={parts()} actions={props.actions} />
              </div>
              <Show when={divider()}>
                <div data-slot="session-turn-compaction">
                  <MessageDivider label={divider()} />
                </div>
              </Show>
              <Show when={showThinkingDropdown()}>
                <div
                  data-slot="session-turn-thinking"
                  data-expanded={thinkingOpen() ? "" : undefined}
                  data-has-layer={hasThinkingLayer() ? "" : undefined}
                >
                  <button
                    type="button"
                    data-slot="session-turn-thinking-trigger"
                    aria-expanded={hasThinkingLayer() ? thinkingOpen() : undefined}
                    disabled={!hasThinkingLayer()}
                    onClick={() => {
                      if (!hasThinkingLayer()) return
                      setThinkingTouched(true)
                      setThinkingOpen((open) => !open)
                    }}
                  >
                    <ThinkingStatus
                      working={working()}
                      createdAt={message()?.time.created}
                      durationMs={turnDurationMs()}
                    />
                    <Show when={hasThinkingLayer()}>
                      <Icon name="chevron-right" size="normal" class="session-turn-thinking-chevron" />
                    </Show>
                  </button>
                  <Show when={hasThinkingLayer() && thinkingOpen()}>
                    <div data-slot="session-turn-thinking-body">
                      <AssistantParts
                        messages={assistantMessages()}
                        turnDurationMs={turnDurationMs()}
                        working={working()}
                        showReasoningSummaries={showReasoningSummaries()}
                        shellToolDefaultOpen={props.shellToolDefaultOpen}
                        editToolDefaultOpen={props.editToolDefaultOpen}
                        includePart={isThinkingTimelinePart}
                        showAssistantCopyPartID={null}
                        suppressArtifactPreviews={true}
                        showAllActivity={true}
                      />
                    </div>
                  </Show>
                </div>
              </Show>
              <Show when={assistantMessages().length > 0 && assistantDerived().assistantContent}>
                <div data-slot="session-turn-assistant-content" aria-hidden={working()}>
                  <AssistantParts
                    messages={assistantMessages()}
                    showAssistantCopyPartID={assistantCopyPartID()}
                    turnDurationMs={turnDurationMs()}
                    working={working()}
                    showReasoningSummaries={showReasoningSummaries()}
                    shellToolDefaultOpen={props.shellToolDefaultOpen}
                    editToolDefaultOpen={props.editToolDefaultOpen}
                    includePart={isAssistantContentPart}
                  />
                </div>
              </Show>
              <Show
                when={
                  !working() &&
                  completion()?.policy !== "observe" &&
                  completion()?.verdict !== "not_required" &&
                  completion()
                }
              >
                {(evidence) => (
                  <div data-slot="session-turn-verification" data-verdict={evidence().verdict} class="mt-2">
                    <button
                      type="button"
                      class="flex items-center gap-1 text-12-regular text-text-weak disabled:cursor-default"
                      disabled={!completionHasDetails()}
                      aria-expanded={completionHasDetails() ? completionOpen() : undefined}
                      aria-label={
                        !completionHasDetails()
                          ? completionLabel()
                          : `${completionLabel()}. ${i18n.t(
                              completionOpen()
                                ? "ui.sessionTurn.verification.details.hide"
                                : "ui.sessionTurn.verification.details.show",
                            )}`
                      }
                      onClick={() => setCompletionOpen((open) => !open)}
                    >
                      <Icon
                        name={
                          evidence().verdict === "passed"
                            ? "circle-check"
                            : evidence().verdict === "failed"
                              ? "circle-x"
                              : "help"
                        }
                        size="small"
                        classList={{
                          "text-icon-success-base": evidence().verdict === "passed",
                          "text-icon-critical-base": evidence().verdict === "failed",
                          "text-icon-weak": evidence().verdict === "unverified",
                        }}
                      />
                      <span>{completionLabel()}</span>
                      <Show when={completionHasDetails()}>
                        <span>
                          ·{" "}
                          {i18n.t(
                            completionDetailCount() === 1
                              ? "ui.sessionTurn.verification.checks.one"
                              : "ui.sessionTurn.verification.checks.other",
                            { count: completionDetailCount() },
                          )}
                        </span>
                        <Icon
                          name="chevron-right"
                          size="small"
                          classList={{ "rotate-90": completionOpen() }}
                        />
                      </Show>
                    </button>
                    <Show
                      when={
                        evidence().checks.length === 0 &&
                        evidence().requirements.length === 0 &&
                        evidence().required
                      }
                    >
                      <div class="mt-1 text-12-regular text-text-weaker">
                        {i18n.t("ui.sessionTurn.verification.noChecks")}
                      </div>
                    </Show>
                    <Show when={(evidence().attempts ?? 0) > 0}>
                      <div class="mt-1 text-11-regular text-text-weaker">
                        {i18n.t(
                          (evidence().attempts ?? 0) === 1
                            ? "ui.sessionTurn.verification.attempts.one"
                            : "ui.sessionTurn.verification.attempts.other",
                          { count: evidence().attempts ?? 0 },
                        )}
                      </div>
                    </Show>
                    <Show when={completionHasDetails() && completionOpen()}>
                      <div class="mt-2 flex flex-col gap-2 rounded-md border border-border-weak-base p-2">
                        <Show when={evidence().review.status !== "not_run"}>
                          <div data-slot="session-turn-verification-review" class="min-w-0">
                            <div class="text-12-medium text-text-base">
                              {i18n.t(`ui.sessionTurn.verification.review.${evidence().review.status}`)}
                            </div>
                            <For each={evidence().review.findings}>
                              {(finding) => (
                                <div class="mt-1 border-l-2 border-border-weak-base pl-2 text-11-regular text-text-weak">
                                  <span class="font-medium">{finding.severity}</span> · {finding.message}
                                  <Show when={finding.evidence}>
                                    <div class="mt-0.5 text-text-weaker">{finding.evidence}</div>
                                  </Show>
                                </div>
                              )}
                            </For>
                          </div>
                        </Show>
                        <For each={evidence().requirements}>
                          {(requirement) => (
                            <div data-slot="session-turn-verification-requirement" class="min-w-0">
                              <div class="flex items-center gap-1 text-12-medium text-text-base">
                                <Icon
                                  name={
                                    requirement.status === "passed"
                                      ? "circle-check"
                                      : requirement.status === "failed"
                                        ? "circle-x"
                                        : "help"
                                  }
                                  size="small"
                                  classList={{
                                    "text-icon-success-base": requirement.status === "passed",
                                    "text-icon-critical-base": requirement.status === "failed",
                                    "text-icon-warning-base": requirement.status === "missing" && requirement.required,
                                    "text-icon-weak": requirement.status === "missing" && !requirement.required,
                                  }}
                                />
                                <span>{requirement.name}</span>
                                <Show when={requirement.status === "missing"}>
                                  <span class="text-11-regular text-text-weaker">
                                    ·{
                                      i18n.t(
                                        requirement.required
                                          ? "ui.sessionTurn.verification.requirement.missing"
                                          : "ui.sessionTurn.verification.requirement.optional",
                                      )
                                    }
                                  </span>
                                </Show>
                              </div>
                              <code class="mt-1 block truncate text-11-mono text-text-weaker">
                                {requirement.command}
                              </code>
                            </div>
                          )}
                        </For>
                        <For each={completionChecks()}>
                          {(check) => (
                            <div data-slot="session-turn-verification-check" class="min-w-0">
                              <div class="flex items-center gap-1 text-12-medium text-text-base">
                                <Icon
                                  name={check.status === "passed" ? "circle-check" : "circle-x"}
                                  size="small"
                                  class={check.status === "passed" ? "text-icon-success-base" : "text-icon-critical-base"}
                                />
                                <code class="truncate">{check.command}</code>
                              </div>
                              <div class="mt-1 text-11-regular text-text-weaker">
                                {i18n.t("ui.sessionTurn.verification.exitCode", { code: check.exitCode })}
                              </div>
                              <Show when={check.output.trim()}>
                                <pre class="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-background-weak p-2 text-11-mono text-text-weak">
                                  {check.output}
                                </pre>
                              </Show>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                  </div>
                )}
              </Show>
              <SessionRetry status={status()} show={active()} />
              <Show when={error()}>
                <Card variant="error" class="error-card">
                  {errorText()}
                </Card>
              </Show>
            </div>
          </Show>
          {props.children}
        </div>
      </div>
    </div>
  )
}
